import * as twgl from "twgl.js";
import { findSceneNode, getAllSceneNodes, type CurvaDocument } from "@/document/model/document";
import type { DocumentChange } from "@/document/model/documentChange";
import type { VectorNetwork } from "@/document/model/vectorNetwork";
import { createDefaultCamera, type Camera } from "./camera";

import { createTexturedQuadBuffer, createTriangleBuffer } from "./gl/buffers";
import { createWebGlContext } from "./gl/context";
import { createEllipseProgram, createRoundedRectProgram, createShapeProgram, createTextureProgram } from "./gl/programs";
import type { DisplayClip, DisplayItem, DisplayPaint } from "./scene/displayList";

import {
  clipUniforms,
  displayItemIntersectsViewport,
} from "./scene/displayItemGeometry";
import {
  boundsEqual,
  prepareSceneFrame,
  type PreparedSceneFrame,
} from "./scene/prepareSceneFrame";
import { InteractiveOverlayDrawer } from "./overlays/interactiveOverlayDrawer";
import { enrichSelectionOverlayBounds } from "./overlays/selectionBounds";
import { DisplayItemDrawer } from "./scene/displayItemDrawer";
import { DocumentExporter } from "./export/documentExporter";
import { TileManager, type RenderTile } from "./tiles/TileManager";
import { binDisplayItemsToTiles } from "./tiles/binItemsToTiles";
import { requiresVectorLiveRender, zoomBucketForScale } from "./tiles/zoomBuckets";
import { FrameProfiler } from "./FrameProfiler";
import { wrapRafCallback } from "./renderDebug";
import {
  getRenderTelemetry,
  markRendererDrawScheduled,
  patchRenderTelemetry,
  recordRendererDrawStart,
  resetRenderTelemetry,
} from "./renderTelemetry";
import {
  overlaySceneSignature,
  overlayRequiresFullScene,
  resolveRenderMode,
  requiresPaintOrderLiveCompose,
  splitItemsByRenderClass,
  tileRasterItemsInPaintOrder,
  type RenderMode,
  type SceneCompositionKey,
} from "./sceneComposition";
import { RetainedScene, documentChangeInvolvesPatternLayout } from "./scene/retainedScene";
import {
  createFramebufferTileBuffer,
  tileTextureCoordsForSubrect,
  formatCacheNumber,
  gradientUniformColors,
  gradientUniformPositions,
  neutralTextureUniforms,
  rectVertices,
} from "./renderHelpers";
import type {
  RenderOverlay,
  RenderPreview,
  RenderClearColor,
} from "./overlayTypes";

export type { RenderClearColor } from "./overlayTypes";
export { previewDisplayItemForTest, previewDisplayItemsForTest } from "./scene/prepareSceneFrame";

interface RendererOptions {
  alpha?: boolean;
  preserveDrawingBuffer?: boolean;
}

const TILE_GAP_SCREEN_PX = 8;
/** Camera gestures: fill a few missing tiles per frame without stalling the pan/zoom. */
const CAMERA_TILE_RASTER_BUDGET = 4;

export type { RenderMode } from "./sceneComposition";

export class Renderer {
  private readonly gl: WebGL2RenderingContext;
  private readonly programInfo: twgl.ProgramInfo;
  private readonly ellipseProgramInfo: twgl.ProgramInfo;
  private readonly roundedRectProgramInfo: twgl.ProgramInfo;
  private readonly textureProgramInfo: twgl.ProgramInfo;
  private readonly tileManager = new TileManager();
  private readonly maxTextureSize: number;
  private camera: Camera = createDefaultCamera();
  private tileZoomBucket?: number;
  private exportRendering = false;
  private renderTrianglesDebug = false;
  private tileGapsDebug = false;
  private exportClearColorOverride?: RenderClearColor;
  private frameHandle = 0;
  private pendingRender?: { document: CurvaDocument; overlay: RenderOverlay; mode?: RenderMode };
  private renderCameraOverride?: Camera;
  private renderResolutionOverride?: [number, number];
  private readonly profiler = new FrameProfiler();
  private sceneComposition?: PreparedSceneFrame;
  private sceneCompositionKey?: SceneCompositionKey;
  private preparedSceneCache?: {
    signature: string;
    frame: PreparedSceneFrame;
    source?: "preview-promotion";
  };
  private readonly frameLabelTextureCache = new Map<string, WebGLTexture>();
  private readonly dimensionLabelTextureCache = new Map<string, { texture: WebGLTexture; width: number; height: number }>();
  private readonly texturedQuadBufferCache = new Map<string, twgl.BufferInfo>();
  private readonly triangleBufferCache = new WeakMap<Float32Array | number[], twgl.BufferInfo>();
  private readonly vectorEditCache = new WeakMap<VectorNetwork, { edgeVertices: number[]; edgeZoom: number }>();
  private lastRender?: { document: CurvaDocument; overlay: RenderOverlay };
  private lastInvalidatedDocument?: { documentId: string; revision: number };
  private pendingDocumentChange?: DocumentChange;
  private lastTextPreviewNodeIds = new Set<string>();
  private lastBakeBackdropIntoTiles?: boolean;
  private pendingTileFillContinuation = false;
  private readonly tileBufferCache = new Map<string, twgl.BufferInfo>();
  private readonly retainedScene = new RetainedScene();
  private readonly interactiveOverlayDrawer: InteractiveOverlayDrawer;
  private readonly displayItemDrawer: DisplayItemDrawer;
  private readonly documentExporter: DocumentExporter;

  constructor(private readonly canvas: HTMLCanvasElement, options: RendererOptions = {}) {
    this.gl = createWebGlContext(canvas, options);
    this.programInfo = createShapeProgram(this.gl);
    this.ellipseProgramInfo = createEllipseProgram(this.gl);
    this.roundedRectProgramInfo = createRoundedRectProgram(this.gl);
    this.textureProgramInfo = createTextureProgram(this.gl);
    this.maxTextureSize = this.gl.getParameter(this.gl.MAX_TEXTURE_SIZE) as number;
    const self = this;
    this.interactiveOverlayDrawer = new InteractiveOverlayDrawer({
      gl: this.gl,
      programInfo: this.programInfo,
      ellipseProgramInfo: this.ellipseProgramInfo,
      textureProgramInfo: this.textureProgramInfo,
      get camera() {
        return self.camera;
      },
      canvas: this.canvas,
      dimensionLabelTextureCache: this.dimensionLabelTextureCache,
      frameLabelTextureCache: this.frameLabelTextureCache,
      texturedQuadBufferCache: this.texturedQuadBufferCache,
      vectorEditCache: this.vectorEditCache,
      get renderCameraOverride() {
        return self.renderCameraOverride;
      },
      get renderResolutionOverride() {
        return self.renderResolutionOverride;
      },
      baseUniforms: positionOffset => self.baseUniforms(positionOffset),
      uniforms: (color, positionOffset, clip) => self.uniforms(color, positionOffset, clip),
      uploadCanvasTexture: canvasElement => self.uploadCanvasTexture(canvasElement),
      drawCachedWebGLTexture: (texture, bounds) => self.drawCachedWebGLTexture(texture, bounds),
      viewportDocumentBounds: () => self.viewportDocumentBounds(),
      activeViewportDocumentBounds: () => self.activeViewportDocumentBounds(),
      recordLabelRaster: () => self.profiler.recordLabelRaster(),
      recordLabelCacheHit: () => self.profiler.recordLabelCacheHit(),
      displayTriangleBuffer: vertices => self.displayTriangleBuffer(vertices),
    });
    this.displayItemDrawer = new DisplayItemDrawer({
      gl: this.gl,
      programInfo: this.programInfo,
      ellipseProgramInfo: this.ellipseProgramInfo,
      roundedRectProgramInfo: this.roundedRectProgramInfo,
      textureProgramInfo: this.textureProgramInfo,
      maxTextureSize: this.maxTextureSize,
      get renderTrianglesDebug() {
        return self.renderTrianglesDebug;
      },
      activeCamera: () => self.activeCamera(),
      activeResolution: () => self.activeResolution(),
      activeViewportDocumentBounds: () => self.activeViewportDocumentBounds(),
      baseUniforms: positionOffset => self.baseUniforms(positionOffset),
      paintUniforms: (paint, positionOffset, clip) => self.paintUniforms(paint, positionOffset, clip),
      displayTriangleBuffer: vertices => self.displayTriangleBuffer(vertices),
      shouldRenderBackgroundBlur: () => self.shouldRenderBackgroundBlur(),
      reloadAfterImageLoad: () => self.reloadAfterImageLoad(),
    });
    this.documentExporter = new DocumentExporter({
      canvas: this.canvas,
      gl: this.gl,
      get renderTrianglesDebug() {
        return self.renderTrianglesDebug;
      },
      displayItemDrawer: this.displayItemDrawer,
      setCamera: camera => {
        self.camera = camera;
      },
      resetRenderOverrides: () => {
        self.renderCameraOverride = undefined;
        self.renderResolutionOverride = undefined;
      },
      runExportComposition: (sceneFrame, clearColor) => {
        self.exportRendering = true;
        self.exportClearColorOverride = clearColor;
        try {
          self.composeSceneFrame(sceneFrame, "full");
        } finally {
          self.exportRendering = false;
          self.exportClearColorOverride = undefined;
        }
      },
    });
  }

  private reloadAfterImageLoad(): void {
    if (this.lastRender) {
      this.tileManager.invalidateEverything();
      this.scheduleDraw(this.lastRender.document, this.lastRender.overlay);
    }
  }

  noteDocumentChange(change: DocumentChange | undefined): void {
    this.pendingDocumentChange = change;
    this.retainedScene.noteDocumentChange(change);
  }

  scheduleDraw(document: CurvaDocument, overlay: RenderOverlay, mode?: RenderMode): void {
    this.invalidateChangedDocument(document, overlay);

    this.pendingRender = { document, overlay, mode };
    if (this.frameHandle !== 0) {
      return;
    }

    markRendererDrawScheduled();
    this.frameHandle = requestAnimationFrame(() => {
      wrapRafCallback("raf:renderer:draw", () => {
        this.frameHandle = 0;
        const render = this.pendingRender;
        this.pendingRender = undefined;
        if (render) {
          this.lastRender = { document: render.document, overlay: render.overlay };
          recordRendererDrawStart("scheduled");
          this.draw(render.document, render.overlay, render.mode);
        }
      });
    });
  }

  drawImmediate(document: CurvaDocument, overlay: RenderOverlay, mode?: RenderMode): void {
    this.invalidateChangedDocument(document, overlay);
    if (this.frameHandle !== 0) {
      cancelAnimationFrame(this.frameHandle);
      this.frameHandle = 0;
    }
    this.pendingRender = undefined;
    this.lastRender = { document, overlay };
    recordRendererDrawStart("immediate");
    this.draw(document, overlay, mode);
  }

  setProfilerEnabled(enabled: boolean): void {
    this.profiler.enabled = enabled;
  }

  setProfilerOverlayVisible(visible: boolean): void {
    this.profiler.showOverlay = visible;
  }

  setRenderTrianglesDebug(enabled: boolean): void {
    if (this.renderTrianglesDebug === enabled) {
      return;
    }

    this.renderTrianglesDebug = enabled;
    this.tileManager.invalidateEverything();
    this.preparedSceneCache = undefined;
    this.sceneComposition = undefined;
    this.sceneCompositionKey = undefined;
  }

  setTileGapsDebug(enabled: boolean): void {
    this.tileGapsDebug = enabled;
  }

  getLastFrameProfile() {
    return this.profiler.getLastSample();
  }

  dispose(): void {
    cancelAnimationFrame(this.frameHandle);
    this.frameHandle = 0;
    this.pendingRender = undefined;
    this.lastRender = undefined;
    this.tileBufferCache.clear();
    this.clearFrameLabelTextureCache();
    this.clearDimensionLabelTextureCache();
    this.displayItemDrawer.clearTextTextureCache();
    this.displayItemDrawer.clearImageCache();
    this.displayItemDrawer.clearDropShadowCache();
    this.tileManager.dispose(this.gl);
  }

  invalidateAll(document: CurvaDocument): void {
    this.pendingDocumentChange = undefined;
    this.retainedScene.noteDocumentChange(undefined);
    this.retainedScene.clear();
    this.displayItemDrawer.clearDropShadowCache();
    this.invalidateTextRendering();
    this.sceneComposition = undefined;
    this.sceneCompositionKey = undefined;
    this.preparedSceneCache = undefined;
    this.clearFrameLabelTextureCache();
    this.clearDimensionLabelTextureCache();
    this.tileManager.invalidateEverything();
    for (const node of getAllSceneNodes(document)) {
      this.tileManager.invalidate(node.bounds, "document");
    }
    this.lastInvalidatedDocument = { documentId: document.id, revision: document.revision };
  }

  invalidateTextRendering(): void {
    this.displayItemDrawer.clearTextTextureCache();
    this.preparedSceneCache = undefined;
    this.sceneComposition = undefined;
    this.sceneCompositionKey = undefined;
    this.tileManager.invalidateEverything();
  }

  private invalidateChangedDocument(document: CurvaDocument, overlay: RenderOverlay): void {
    const invalidated = this.lastInvalidatedDocument;
    if (
      invalidated?.documentId === document.id
      && invalidated.revision === document.revision
    ) {
      return;
    }

    const change = this.pendingDocumentChange?.document === document
      ? this.pendingDocumentChange
      : undefined;
    this.pendingDocumentChange = undefined;
    if (change && this.tryPromoteCommittedPreview(document, overlay, change)) {
      return;
    }

    if (change && this.retainedScene.consumeDocumentChange(document, overlay.activePageId)) {
      for (const bounds of change.affectedBounds) {
        this.tileManager.invalidate(bounds, "document-change");
      }
      this.sceneComposition = undefined;
      this.sceneCompositionKey = undefined;
      this.preparedSceneCache = undefined;
      this.lastInvalidatedDocument = { documentId: document.id, revision: document.revision };
      return;
    }

    if (invalidated && invalidated.documentId !== document.id) {
      this.clearDocumentRenderCaches();
    }
    this.invalidateAll(document);
  }

  private tryPromoteCommittedPreview(
    document: CurvaDocument,
    overlay: RenderOverlay,
    change: DocumentChange,
  ): boolean {
    const previousFrame = this.sceneComposition;
    const previousRender = this.lastRender;
    if (
      !previousFrame
      || !previousRender
      || !change.previewCompatible
      || previousFrame.document !== change.previousDocument
      || previousRender.document !== change.previousDocument
      || document.id !== change.previousDocument.id
      || document.revision !== change.previousDocument.revision + 1
      || overlayRequiresFullScene(overlay)
    ) {
      return false;
    }

    const previousOverlay = previousRender.overlay;
    const movePreviews = previousOverlay.previews.filter(
      preview => preview.nodeId !== "__marquee__" && preview.mode !== "copy",
    );
    const previousOverlayWithoutPreviews = {
      ...previousOverlay,
      previews: [],
    };
    const changedNodeIds = new Set(change.changedNodeIds);
    const previewNodeIds = new Set(movePreviews.map(preview => preview.nodeId));
    if (
      movePreviews.length === 0
      || previousOverlay.activePageId !== overlay.activePageId
      || overlayRequiresFullScene(previousOverlayWithoutPreviews)
      || changedNodeIds.size !== previewNodeIds.size
      || [...changedNodeIds].some(nodeId => !previewNodeIds.has(nodeId))
      || documentChangeInvolvesPatternLayout(document, change.changedNodeIds)
      || previousFrame.copyPreviewItems.length > 0
      || previousFrame.draftVectorItems.length > 0
      || previousFrame.draftComponentItems.length > 0
      || previousFrame.vectorPreview
      || previousFrame.textPreviewNodeIds.size > 0
    ) {
      return false;
    }

    for (const preview of movePreviews) {
      const node = findSceneNode(document, preview.nodeId);
      if (
        !node
        || !boundsEqual(node.bounds, preview.bounds)
        || (preview.rotation !== undefined && Math.abs(node.transform.rotation - preview.rotation) > 0.001)
      ) {
        return false;
      }

      // Preview display items are scaled from the committed geometry via a bounds
      // remap, which also scales stroke thickness. That is fine for a transient
      // drag preview, but promoting those items as the committed scene would bake
      // the scaled stroke in permanently. Only pure moves/rotations are safe to
      // promote; a resize must fall through to incremental retessellation so the
      // stroke is regenerated at its true width.
      const previousNode = findSceneNode(change.previousDocument, preview.nodeId);
      if (
        !previousNode
        || Math.abs(previousNode.bounds.width - node.bounds.width) > 0.001
        || Math.abs(previousNode.bounds.height - node.bounds.height) > 0.001
      ) {
        return false;
      }
    }

    const sceneSignature = overlaySceneSignature(overlay, document.revision);
    const committedItems = previousFrame.paintOrderItems;
    const classified = splitItemsByRenderClass(committedItems, new Set());
    const promoted: PreparedSceneFrame = {
      ...previousFrame,
      document,
      revision: document.revision,
      sceneSignature,
      tileableItems: classified.tileable,
      liveRasterItems: classified.liveRaster,
      liveBackdropItems: classified.liveBackdrop,
      dynamicItems: classified.dynamic,
      copyPreviewItems: [],
      draftVectorItems: [],
      draftComponentItems: [],
      paintOrderItems: committedItems,
      overlayLookupItems: committedItems,
      vectorPreview: undefined,
      textPreviewNodeIds: new Set(),
    };

    for (const bounds of change.affectedBounds) {
      this.tileManager.invalidate(bounds, "document-change");
    }
    for (const item of previousFrame.dynamicItems) {
      this.tileManager.invalidate(item.visualBounds ?? item.bounds, "preview-commit");
    }

    this.retainedScene.adoptDisplayList(document, { items: committedItems }, overlay.activePageId);
    this.preparedSceneCache = {
      signature: sceneSignature,
      frame: promoted,
      source: "preview-promotion",
    };
    this.sceneComposition = promoted;
    this.sceneCompositionKey = {
      document,
      revision: document.revision,
      activePageId: overlay.activePageId,
      sceneSignature,
      tileVariantKey: promoted.tileVariantKey,
    };
    this.lastInvalidatedDocument = { documentId: document.id, revision: document.revision };
    return true;
  }

  private clearDocumentRenderCaches(): void {
    this.pendingRender = undefined;
    this.lastRender = undefined;
    this.sceneComposition = undefined;
    this.sceneCompositionKey = undefined;
    this.preparedSceneCache = undefined;
    this.retainedScene.clear();
    this.lastTextPreviewNodeIds.clear();
    this.lastBakeBackdropIntoTiles = undefined;
    this.tileBufferCache.clear();
    this.clearFrameLabelTextureCache();
    this.clearDimensionLabelTextureCache();
    this.displayItemDrawer.clearTextTextureCache();
    this.displayItemDrawer.clearImageCache();
    this.displayItemDrawer.clearDropShadowCache();
    this.tileManager.dispose(this.gl);
  }

  setCamera(camera: Camera): void {
    this.camera = camera;
  }

  setTileZoomBucket(bucket: number): void {
    this.tileZoomBucket = bucket;
  }

  private activeTileZoomBucket(): number {
    const scale = this.camera.zoom * this.camera.devicePixelRatio;
    return this.tileZoomBucket ?? zoomBucketForScale(scale);
  }

  private shouldRenderBackgroundBlur(): boolean {
    if (this.exportRendering) {
      return true;
    }
    // Match the frozen tile bucket during pan/zoom gestures so bake-vs-live backdrop
    // mode does not flip while tiles are rasterized at a stable bucket scale.
    return this.activeTileZoomBucket() >= 0.5;
  }

  stabilizeAfterCameraGesture(): void {
    this.tileBufferCache.clear();
    // Drop-shadow textures are already keyed by raster scale and bounded by an
    // LRU. Deleting them here can synchronously stall on the GPU—especially
    // after a far-zoom frame populated many shadows. Let incompatible scales
    // age out through normal cache eviction instead.
    this.tileManager.invalidateEverything();
  }

  async renderDocumentToPngBlob(document: CurvaDocument, fallbackBounds?: RenderPreview["bounds"], scale = 1): Promise<Blob> {
    return this.documentExporter.renderDocumentToPngBlob(document, fallbackBounds, scale);
  }

  async renderDocumentToCanvas(
    document: CurvaDocument,
    fallbackBounds?: RenderPreview["bounds"],
    scale = 1,
    options: { clearColor?: RenderClearColor } = {},
  ): Promise<void> {
    return this.documentExporter.renderDocumentToCanvas(document, fallbackBounds, scale, options);
  }

  async renderDocumentThumbnail(document: CurvaDocument, contentBounds: RenderPreview["bounds"], boxSize: number): Promise<Blob> {
    return this.documentExporter.renderDocumentThumbnail(document, contentBounds, boxSize);
  }

  private draw(document: CurvaDocument, overlay: RenderOverlay, explicitMode?: RenderMode): void {
    resetRenderTelemetry();
    const sceneSignature = overlaySceneSignature(overlay, document.revision);
    const provisionalKey = this.sceneComposition?.tileVariantKey ?? "classified";
    const renderMode = resolveRenderMode(overlay, document, this.sceneCompositionKey, provisionalKey, explicitMode);
    patchRenderTelemetry({ renderMode });
    this.profiler.beginFrame(renderMode === "camera");

    let sceneFrame: PreparedSceneFrame;
    if (renderMode === "camera" && this.sceneComposition) {
      sceneFrame = this.sceneComposition;
      patchRenderTelemetry({ prepareCache: "camera-reuse" });
      this.profiler.beginPrepare();
      this.profiler.endPrepare();
    } else if (this.preparedSceneCache?.signature === sceneSignature) {
      sceneFrame = this.preparedSceneCache.frame;
      patchRenderTelemetry({
        prepareCache: this.preparedSceneCache.source ?? "signature-cache",
      });
      this.preparedSceneCache.source = undefined;
      this.profiler.beginPrepare();
      this.profiler.endPrepare();
    } else {
      patchRenderTelemetry({ prepareCache: "fresh" });
      this.profiler.beginPrepare();
      sceneFrame = prepareSceneFrame(document, overlay, sceneSignature, {
        renderTrianglesDebug: this.renderTrianglesDebug,
        retainedScene: this.retainedScene,
      });
      this.preparedSceneCache = { signature: sceneSignature, frame: sceneFrame };
      this.profiler.endPrepare();
    }

    this.sceneComposition = sceneFrame;
    this.sceneCompositionKey = {
      document,
      revision: document.revision,
      activePageId: overlay.activePageId,
      sceneSignature,
      tileVariantKey: sceneFrame.tileVariantKey,
    };

    this.profiler.beginCompose();
    const tileStats = this.composeSceneFrame(sceneFrame, renderMode);
    this.profiler.endCompose();
    this.profiler.recordTiles(tileStats.dirtyTileCount, tileStats.displayTileCount);
    this.profiler.recordLiveItemCount(tileStats.liveItemCount);

    if (!this.tileGapsDebug) {
      this.profiler.beginOverlay();
      this.drawInteractiveOverlays(document, overlay, sceneFrame);
      if (overlay.showRenderDebug && this.profiler.showOverlay) {
        this.drawRenderDebugOverlay();
      }
      this.profiler.endOverlay();
    }
    this.profiler.endFrame(getRenderTelemetry());

    if (this.pendingTileFillContinuation && this.lastRender && !this.exportRendering) {
      this.pendingTileFillContinuation = false;
      this.scheduleDraw(this.lastRender.document, this.lastRender.overlay, explicitMode ?? "camera");
    }
  }


  private invalidateTilesForMovePreviews(document: CurvaDocument, sceneFrame: PreparedSceneFrame): void {
    if (sceneFrame.dynamicItems.length === 0) {
      return;
    }

    for (const item of sceneFrame.dynamicItems) {
      const node = getAllSceneNodes(document).find(candidate => candidate.id === item.id);
      if (node) {
        this.tileManager.invalidate(node.bounds, "move-preview");
      }
    }
  }

  private invalidateTilesForTextPreviews(document: CurvaDocument, textPreviewNodeIds: Set<string>): void {
    const currentIds = textPreviewNodeIds;
    const affectedIds = new Set([...currentIds, ...this.lastTextPreviewNodeIds]);
    this.lastTextPreviewNodeIds = new Set(currentIds);
    if (affectedIds.size === 0) {
      return;
    }

    for (const nodeId of affectedIds) {
      const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
      if (node) {
        this.tileManager.invalidate(node.bounds, "text-preview");
      }
    }
  }

  private composeExportFrame(sceneFrame: PreparedSceneFrame): { dirtyTileCount: number; displayTileCount: number; liveItemCount: number } {
    const gl = this.gl;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    this.renderCameraOverride = undefined;
    this.renderResolutionOverride = undefined;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
    const [clearR, clearG, clearB, clearA] = this.sceneClearColor();
    gl.clearColor(clearR, clearG, clearB, clearA);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const viewportBounds = this.viewportDocumentBounds();
    const items = sceneFrame.paintOrderItems.filter(item => displayItemIntersectsViewport(item, viewportBounds));
    this.displayItemDrawer.drawDisplayItems(items);
    gl.disable(gl.SCISSOR_TEST);

    return {
      dirtyTileCount: 0,
      displayTileCount: 0,
      liveItemCount: items.length,
    };
  }

  private composeSceneFrame(
    sceneFrame: PreparedSceneFrame,
    renderMode: RenderMode = "full",
  ): { dirtyTileCount: number; displayTileCount: number; liveItemCount: number } {
    if (this.exportRendering) {
      return this.composeExportFrame(sceneFrame);
    }

    if (this.renderTrianglesDebug) {
      return this.composeTriangleDebugFrame(sceneFrame);
    }

    if (this.tileGapsDebug) {
      return this.composeTileGapsDebugFrame(sceneFrame);
    }

    if (!this.exportRendering) {
      this.resizeCanvas();
    }

    const gl = this.gl;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    const viewportBounds = this.viewportDocumentBounds();
    const displayScale = this.camera.zoom * this.camera.devicePixelRatio;
    const vectorLive = requiresVectorLiveRender(displayScale);
    const renderBackgroundBlurLive = this.shouldRenderBackgroundBlur();
    const visibleBackdropItems = renderBackgroundBlurLive
      ? sceneFrame.liveBackdropItems.filter(item => displayItemIntersectsViewport(item, viewportBounds))
      : [];
    const paintOrderLive = requiresPaintOrderLiveCompose(
      sceneFrame.dynamicItems.length,
      sceneFrame.copyPreviewItems.length,
      visibleBackdropItems.length,
    );
    this.invalidateTilesForMovePreviews(sceneFrame.document, sceneFrame);
    this.invalidateTilesForTextPreviews(sceneFrame.document, sceneFrame.textPreviewNodeIds);
    const bakeBackdropIntoTiles = !renderBackgroundBlurLive;
    if (this.lastBakeBackdropIntoTiles !== undefined && this.lastBakeBackdropIntoTiles !== bakeBackdropIntoTiles) {
      this.tileManager.invalidateEverything();
      this.tileBufferCache.clear();
    }
    this.lastBakeBackdropIntoTiles = bakeBackdropIntoTiles;

    const [clearR, clearG, clearB, clearA] = this.sceneClearColor();

    if (vectorLive || paintOrderLive) {
      this.renderCameraOverride = undefined;
      this.renderResolutionOverride = undefined;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
      gl.clearColor(clearR, clearG, clearB, clearA);
      gl.clear(gl.COLOR_BUFFER_BIT);
      const liveItems = paintOrderLive
        ? [
          ...sceneFrame.paintOrderItems,
          ...sceneFrame.draftVectorItems,
          ...sceneFrame.draftComponentItems,
        ]
        : [
          ...sceneFrame.tileableItems,
          ...sceneFrame.liveRasterItems,
          ...sceneFrame.liveBackdropItems,
          ...sceneFrame.draftVectorItems,
          ...sceneFrame.draftComponentItems,
        ];
      const visibleLiveItems = liveItems.filter(item => displayItemIntersectsViewport(item, viewportBounds));
      const liveStart = performance.now();
      this.displayItemDrawer.drawDisplayItems(visibleLiveItems);
      patchRenderTelemetry({ composeLiveMs: performance.now() - liveStart });
      gl.disable(gl.SCISSOR_TEST);
      return {
        dirtyTileCount: 0,
        displayTileCount: 0,
        liveItemCount: visibleLiveItems.length,
      };
    }

    const dynamicItemIds = new Set(sceneFrame.dynamicItems.map(item => item.id));
    const tileItems = tileRasterItemsInPaintOrder(
      sceneFrame.paintOrderItems,
      dynamicItemIds,
      bakeBackdropIntoTiles,
    );
    const tileStart = performance.now();
    const frameTiles = this.renderSceneTiles(tileItems, sceneFrame.tileVariantKey, {
      maxRasterize: renderMode === "camera" ? CAMERA_TILE_RASTER_BUDGET : undefined,
    });
    this.pendingTileFillContinuation = frameTiles.incomplete && !this.exportRendering;
    patchRenderTelemetry({ composeTilesMs: performance.now() - tileStart });
    this.renderCameraOverride = undefined;
    this.renderResolutionOverride = undefined;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
    gl.clearColor(clearR, clearG, clearB, clearA);
    gl.clear(gl.COLOR_BUFFER_BIT);
    this.drawTiles(frameTiles.display);

    const liveItems = [
      ...sceneFrame.liveRasterItems,
      ...sceneFrame.dynamicItems,
      ...sceneFrame.copyPreviewItems,
      ...sceneFrame.draftVectorItems,
      ...sceneFrame.draftComponentItems,
    ].filter(item => displayItemIntersectsViewport(item, viewportBounds));
    const liveStart = performance.now();
    this.displayItemDrawer.drawDisplayItems(liveItems);
    patchRenderTelemetry({ composeLiveMs: performance.now() - liveStart });
    const backdropItems = renderBackgroundBlurLive && !paintOrderLive
      ? visibleBackdropItems
      : [];
    if (backdropItems.length > 0) {
      const backdropStart = performance.now();
      this.displayItemDrawer.drawDisplayItems(backdropItems);
      patchRenderTelemetry({ composeBackdropMs: performance.now() - backdropStart });
    }
    gl.disable(gl.SCISSOR_TEST);

    return {
      dirtyTileCount: frameTiles.rasterize.length,
      displayTileCount: frameTiles.display.length,
      liveItemCount: liveItems.length + backdropItems.length,
    };
  }

  private composeTileGapsDebugFrame(sceneFrame: PreparedSceneFrame): { dirtyTileCount: number; displayTileCount: number; liveItemCount: number } {
    this.resizeCanvas();

    const gl = this.gl;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    this.invalidateTilesForMovePreviews(sceneFrame.document, sceneFrame);
    this.invalidateTilesForTextPreviews(sceneFrame.document, sceneFrame.textPreviewNodeIds);
    const bakeBackdropIntoTiles = !this.shouldRenderBackgroundBlur();
    const dynamicItemIds = new Set(sceneFrame.dynamicItems.map(item => item.id));
    const tileItems = tileRasterItemsInPaintOrder(
      sceneFrame.paintOrderItems,
      dynamicItemIds,
      bakeBackdropIntoTiles,
    );
    const frameTiles = this.renderSceneTiles(tileItems, sceneFrame.tileVariantKey);

    this.renderCameraOverride = undefined;
    this.renderResolutionOverride = undefined;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
    gl.clearColor(0.067, 0.071, 0.078, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    this.drawTilesWithGaps(frameTiles.display);
    gl.disable(gl.SCISSOR_TEST);

    return {
      dirtyTileCount: frameTiles.rasterize.length,
      displayTileCount: frameTiles.display.length,
      liveItemCount: 0,
    };
  }

  private composeTriangleDebugFrame(sceneFrame: PreparedSceneFrame): { dirtyTileCount: number; displayTileCount: number; liveItemCount: number } {
    this.resizeCanvas();

    const gl = this.gl;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    this.renderCameraOverride = undefined;
    this.renderResolutionOverride = undefined;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
    const [clearR, clearG, clearB, clearA] = this.sceneClearColor();
    gl.clearColor(clearR, clearG, clearB, clearA);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const viewportBounds = this.viewportDocumentBounds();
    const items = [
      ...sceneFrame.paintOrderItems,
      ...sceneFrame.draftVectorItems,
      ...sceneFrame.draftComponentItems,
    ].filter(item => displayItemIntersectsViewport(item, viewportBounds));
    this.displayItemDrawer.drawDisplayItems(items);
    gl.disable(gl.SCISSOR_TEST);

    return {
      dirtyTileCount: 0,
      displayTileCount: 0,
      liveItemCount: items.length,
    };
  }


  private drawInteractiveOverlays(document: CurvaDocument, overlay: RenderOverlay, sceneFrame: PreparedSceneFrame): void {
    this.interactiveOverlayDrawer.draw(document, enrichSelectionOverlayBounds(document, overlay, sceneFrame), sceneFrame);
  }

  private displayTriangleBuffer(vertices: Float32Array | number[]): twgl.BufferInfo {
    const cached = this.triangleBufferCache.get(vertices);
    if (cached) {
      return cached;
    }

    const buffer = createTriangleBuffer(this.gl, vertices);
    this.triangleBufferCache.set(vertices, buffer);
    return buffer;
  }

  private renderSceneTiles(
    items: DisplayItem[],
    variantKey: string,
    options: { maxRasterize?: number } = {},
  ): { display: RenderTile[]; rasterize: RenderTile[]; incomplete: boolean } {
    const frameTiles = this.tileManager.beginFrame(this.gl, this.camera, this.canvas, variantKey, {
      tileZoomBucket: this.activeTileZoomBucket(),
      maxRasterize: options.maxRasterize,
    });
    if (frameTiles.rasterize.length === 0) {
      return frameTiles;
    }

    const visibleTiles = [...frameTiles.display, ...frameTiles.rasterize];
    const itemBins = binDisplayItemsToTiles(items, visibleTiles);

    for (const tile of frameTiles.rasterize) {
      this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, tile.framebuffer);
      this.gl.viewport(0, 0, tile.pixelSize, tile.pixelSize);
      this.gl.clearColor(0, 0, 0, 0);
      this.gl.clear(this.gl.COLOR_BUFFER_BIT);
      // Tile textures are composited as premultiplied-alpha textures. Preserve
      // straight source blending for RGB while accumulating coverage in alpha,
      // so static translucent content can be baked without double-applying alpha.
      this.gl.blendFuncSeparate(
        this.gl.SRC_ALPHA,
        this.gl.ONE_MINUS_SRC_ALPHA,
        this.gl.ONE,
        this.gl.ONE_MINUS_SRC_ALPHA,
      );
      this.renderCameraOverride = {
        x: tile.bounds.x,
        y: tile.bounds.y,
        zoom: tile.zoomBucket,
        devicePixelRatio: 1,
      };
      this.renderResolutionOverride = [tile.pixelSize, tile.pixelSize];
      this.displayItemDrawer.drawDisplayItems(itemBins.get(tile.key) ?? []);
      tile.dirty = false;
    }
    this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);

    const display = frameTiles.display.filter(
      tile => !frameTiles.rasterize.some(rasterized => boundsEqual(tile.bounds, rasterized.bounds)),
    );
    for (const tile of frameTiles.rasterize) {
      display.push({
        ...tile,
        compositeScale: 1,
        sourceZoomBucket: tile.zoomBucket,
        sourceBounds: undefined,
      });
    }
    return { display, rasterize: frameTiles.rasterize, incomplete: frameTiles.incomplete };
  }

  private sceneClearColor(): [number, number, number, number] {
    if (this.exportRendering && this.exportClearColorOverride) {
      return this.exportClearColorOverride;
    }
    return [0.075, 0.079, 0.086, 1];
  }

  private drawTiles(tiles: RenderTile[]): void {
    this.drawTileTextures(tiles, tile => this.tileBuffer(tile));
  }

  private drawTilesWithGaps(tiles: RenderTile[]): void {
    this.drawTileTextures(tiles, tile => this.tileBufferWithGaps(tile));
  }

  private drawTileTextures(tiles: RenderTile[], bufferForTile: (tile: RenderTile) => twgl.BufferInfo): void {
    this.gl.blendFunc(this.gl.ONE, this.gl.ONE_MINUS_SRC_ALPHA);
    this.gl.useProgram(this.textureProgramInfo.program);
    for (const tile of tiles) {
      const buffer = bufferForTile(tile);
      twgl.setBuffersAndAttributes(this.gl, this.textureProgramInfo, buffer);
      twgl.setUniforms(this.textureProgramInfo, {
        ...this.baseUniforms(),
        u_texture: tile.texture,
        ...neutralTextureUniforms(),
      });
      twgl.drawBufferInfo(this.gl, buffer);
    }
    this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);
    this.gl.useProgram(this.programInfo.program);
  }

  private tileBuffer(tile: RenderTile): twgl.BufferInfo {
    const sourceKey = tile.sourceBounds
      ? `${tile.sourceBounds.x}:${tile.sourceBounds.y}:${tile.sourceBounds.width}:${tile.sourceBounds.height}`
      : "";
    const bufferKey = `${tile.key}:${tile.bounds.x}:${tile.bounds.y}:${tile.bounds.width}:${tile.bounds.height}:${tile.compositeScale}:${sourceKey}`;
    const cached = this.tileBufferCache.get(bufferKey);
    if (cached) {
      return cached;
    }

    const vertices = this.tileVertices(tile);
    const texcoords = tile.sourceBounds
      ? tileTextureCoordsForSubrect(tile.bounds, tile.sourceBounds)
      : undefined;
    const buffer = createFramebufferTileBuffer(this.gl, vertices, texcoords);
    this.tileBufferCache.set(bufferKey, buffer);
    return buffer;
  }

  private tileBufferWithGaps(tile: RenderTile): twgl.BufferInfo {
    const sourceKey = tile.sourceBounds
      ? `${tile.sourceBounds.x}:${tile.sourceBounds.y}:${tile.sourceBounds.width}:${tile.sourceBounds.height}`
      : "";
    const gapOffset = this.tileGapDocumentOffset(tile);
    const bufferKey = `${tile.key}:gaps:${gapOffset.x}:${gapOffset.y}:${tile.compositeScale}:${sourceKey}`;
    const cached = this.tileBufferCache.get(bufferKey);
    if (cached) {
      return cached;
    }

    const vertices = this.tileVerticesWithGaps(tile);
    const texcoords = tile.sourceBounds
      ? tileTextureCoordsForSubrect(tile.bounds, tile.sourceBounds)
      : undefined;
    const buffer = createFramebufferTileBuffer(this.gl, vertices, texcoords);
    this.tileBufferCache.set(bufferKey, buffer);
    return buffer;
  }

  private tileVertices(tile: RenderTile): number[] {
    if (tile.compositeScale === 1) {
      return rectVertices(tile.bounds);
    }

    const centerX = tile.bounds.x + tile.bounds.width / 2;
    const centerY = tile.bounds.y + tile.bounds.height / 2;
    const width = tile.bounds.width * tile.compositeScale;
    const height = tile.bounds.height * tile.compositeScale;
    return rectVertices({
      x: centerX - width / 2,
      y: centerY - height / 2,
      width,
      height,
    });
  }

  private tileVerticesWithGaps(tile: RenderTile): number[] {
    const gapOffset = this.tileGapDocumentOffset(tile);
    const bounds = {
      x: tile.bounds.x + gapOffset.x,
      y: tile.bounds.y + gapOffset.y,
      width: tile.bounds.width,
      height: tile.bounds.height,
    };

    if (tile.compositeScale === 1) {
      return rectVertices(bounds);
    }

    const centerX = bounds.x + bounds.width / 2;
    const centerY = bounds.y + bounds.height / 2;
    const width = bounds.width * tile.compositeScale;
    const height = bounds.height * tile.compositeScale;
    return rectVertices({
      x: centerX - width / 2,
      y: centerY - height / 2,
      width,
      height,
    });
  }

  private tileGapDocumentOffset(tile: RenderTile): { x: number; y: number } {
    const scale = this.camera.zoom * this.camera.devicePixelRatio;
    const gap = TILE_GAP_SCREEN_PX / scale;
    return { x: tile.tileX * gap, y: tile.tileY * gap };
  }

  private baseUniforms(positionOffset: { x: number; y: number } = { x: 0, y: 0 }): Record<string, unknown> {
    const camera = this.renderCameraOverride ?? this.camera;
    const resolution = this.renderResolutionOverride ?? [this.canvas.width, this.canvas.height];
    return {
      u_resolution: resolution,
      u_camera: [camera.x, camera.y],
      u_zoom: camera.zoom * camera.devicePixelRatio,
      u_positionOffset: [positionOffset.x, positionOffset.y],
    };
  }

  private uniforms(
    color: [number, number, number, number],
    positionOffset?: { x: number; y: number },
    clip?: DisplayClip,
  ): Record<string, unknown> {
    return {
      ...this.baseUniforms(positionOffset),
      u_paintType: 0,
      u_color: color,
      u_gradientStart: [0, 0],
      u_gradientEnd: [1, 0],
      u_radialCenter: [0, 0],
      u_radialRadius: 1,
      u_gradientStopCount: 1,
      u_gradientPositions: new Array<number>(32).fill(0),
      u_gradientColors: gradientUniformColors([color]),
      ...clipUniforms(clip),
    };
  }

  private paintUniforms(paint: DisplayPaint, positionOffset?: { x: number; y: number }, clip?: DisplayClip): Record<string, unknown> {
    if (paint.type === "solid") {
      return this.uniforms(paint.color, positionOffset, clip);
    }

    if (paint.type === "image") {
      return this.uniforms([0, 0, 0, 0], positionOffset, clip);
    }

    const stops = paint.stops.slice(0, 32);
    if (paint.type === "radialGradient") {
      return {
        ...this.baseUniforms(positionOffset),
        u_paintType: 2,
        u_color: stops[0]?.color ?? [0, 0, 0, 1],
        u_gradientStart: [0, 0],
        u_gradientEnd: [1, 0],
        u_radialCenter: paint.center,
        u_radialRadius: paint.radius,
        u_gradientStopCount: Math.max(1, stops.length),
        u_gradientPositions: gradientUniformPositions(stops),
        u_gradientColors: gradientUniformColors(stops.map(stop => stop.color)),
        ...clipUniforms(clip),
      };
    }

    return {
      ...this.baseUniforms(positionOffset),
      u_paintType: 1,
      u_color: stops[0]?.color ?? [0, 0, 0, 1],
      u_gradientStart: paint.start,
      u_gradientEnd: paint.end,
      u_radialCenter: [0, 0],
      u_radialRadius: 1,
      u_gradientStopCount: Math.max(1, stops.length),
      u_gradientPositions: gradientUniformPositions(stops),
      u_gradientColors: gradientUniformColors(stops.map(stop => stop.color)),
      ...clipUniforms(clip),
    };
  }

  private viewportDocumentBounds(): RenderPreview["bounds"] {
    const scale = this.camera.zoom * this.camera.devicePixelRatio;
    return {
      x: this.camera.x,
      y: this.camera.y,
      width: this.canvas.width / scale,
      height: this.canvas.height / scale,
    };
  }

  private activeViewportDocumentBounds(): RenderPreview["bounds"] {
    const camera = this.activeCamera();
    const [resolutionWidth, resolutionHeight] = this.activeResolution();
    const scale = camera.zoom * camera.devicePixelRatio;
    return {
      x: camera.x,
      y: camera.y,
      width: resolutionWidth / scale,
      height: resolutionHeight / scale,
    };
  }

  private activeCamera(): Camera {
    return this.renderCameraOverride ?? this.camera;
  }

  private activeResolution(): [number, number] {
    return this.renderResolutionOverride ?? [this.canvas.width, this.canvas.height];
  }

  private resizeCanvas(): void {
    if (this.exportRendering) {
      return;
    }

    const width = Math.max(1, Math.floor(this.canvas.clientWidth * this.camera.devicePixelRatio));
    const height = Math.max(1, Math.floor(this.canvas.clientHeight * this.camera.devicePixelRatio));

    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
  }


  private uploadCanvasTexture(canvas: HTMLCanvasElement): WebGLTexture | undefined {
    const texture = this.gl.createTexture();
    if (!texture) {
      return undefined;
    }

    this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
    this.gl.pixelStorei(this.gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA, this.gl.UNSIGNED_BYTE, canvas);
    return texture;
  }

  private drawCachedWebGLTexture(texture: WebGLTexture, bounds: RenderPreview["bounds"] & { corners?: Array<{ x: number; y: number }> }): void {
    const vertices = bounds.corners && bounds.corners.length >= 4
      ? [
        bounds.corners[0]!.x,
        bounds.corners[0]!.y,
        bounds.corners[1]!.x,
        bounds.corners[1]!.y,
        bounds.corners[3]!.x,
        bounds.corners[3]!.y,
        bounds.corners[3]!.x,
        bounds.corners[3]!.y,
        bounds.corners[1]!.x,
        bounds.corners[1]!.y,
        bounds.corners[2]!.x,
        bounds.corners[2]!.y,
      ]
      : rectVertices(bounds);
    const bufferKey = `${vertices.map(value => formatCacheNumber(value)).join(":")}`;
    let buffer = this.texturedQuadBufferCache.get(bufferKey);
    if (!buffer) {
      buffer = createTexturedQuadBuffer(this.gl, vertices);
      this.texturedQuadBufferCache.set(bufferKey, buffer);
    }

    this.gl.useProgram(this.textureProgramInfo.program);
    twgl.setBuffersAndAttributes(this.gl, this.textureProgramInfo, buffer);
    twgl.setUniforms(this.textureProgramInfo, {
      ...this.baseUniforms(),
      u_texture: texture,
      ...neutralTextureUniforms(),
    });
    twgl.drawBufferInfo(this.gl, buffer);
    this.gl.useProgram(this.programInfo.program);
  }

  private clearFrameLabelTextureCache(): void {
    for (const texture of this.frameLabelTextureCache.values()) {
      this.gl.deleteTexture(texture);
    }
    this.frameLabelTextureCache.clear();
  }

  private clearDimensionLabelTextureCache(): void {
    for (const cached of this.dimensionLabelTextureCache.values()) {
      this.gl.deleteTexture(cached.texture);
    }
    this.dimensionLabelTextureCache.clear();
  }

  private drawRenderDebugOverlay(): void {
    const overlay = document.createElement("canvas");
    overlay.width = 360;
    overlay.height = 88;
    const context = overlay.getContext("2d");
    if (!context) {
      return;
    }

    context.fillStyle = "rgba(0, 0, 0, 0.72)";
    context.fillRect(0, 0, overlay.width, overlay.height);
    context.fillStyle = "#d1fae5";
    context.font = "12px ui-monospace, SFMono-Regular, Menlo, monospace";
    for (const [index, line] of this.profiler.formatOverlayText().split("\n").entries()) {
      context.fillText(line, 10, 18 + index * 16);
    }

    const bounds = {
      x: this.camera.x + 12 / this.camera.zoom,
      y: this.camera.y + 12 / this.camera.zoom,
      width: overlay.width / this.camera.zoom,
      height: overlay.height / this.camera.zoom,
    };
    const texture = this.uploadCanvasTexture(overlay);
    if (texture) {
      this.drawCachedWebGLTexture(texture, bounds);
      this.gl.deleteTexture(texture);
    }
  }

}
